/**
 * Tab 3: Usage Analytics
 * Operational metrics for leadership and AI governance reporting.
 */

import React, { useState } from 'react';
import { LoadingPanel, ErrorPanel } from '../shared/LoadingPanel';
import { useMetrics, useFeedback, formatRelativeTime } from '../hooks/useAdminData';

/** Format response time from milliseconds to human-readable. */
function formatResponseTime(ms: number | undefined | null): string {
  if (ms == null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

const TIME_RANGES: Array<{ label: string; hours: number }> = [
  { label: '7 days', hours: 168 },
  { label: '30 days', hours: 720 },
  { label: '90 days', hours: 2160 },
];

// Simple SVG bar chart — no external dependency
function BarChart({ data, height = 120 }: { data: Array<{ label: string; value: number }>; height?: number }) {
  if (data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.value), 1);
  const barWidth = Math.max(4, Math.min(18, Math.floor(500 / data.length) - 2));

  return (
    <svg
      viewBox={`0 0 ${data.length * (barWidth + 2) + 20} ${height + 24}`}
      style={{ width: '100%', maxHeight: height + 24, display: 'block' }}
    >
      {data.map((d, i) => {
        const barH = (d.value / max) * height;
        const x = i * (barWidth + 2) + 10;
        const y = height - barH;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barH}
              rx={2}
              style={{ fill: 'var(--doyle-blue)' }}
              opacity={0.85}
            >
              <title>{`${d.label}: ${d.value}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}

// Simple donut chart for model routing
function DonutChart({
  segments,
  size = 100,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  size?: number;
}) {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const r = (size - 10) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        {segments.map((seg, i) => {
          const pct = seg.value / total;
          const dash = pct * circumference;
          const currentOffset = offset;
          offset += dash;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              style={{ stroke: seg.color }}
              strokeWidth={12}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-currentOffset}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          );
        })}
        <text x={cx} y={cy + 4} textAnchor="middle" fontSize="14" fontFamily="'Roboto Mono', monospace" fontWeight="700" fill="#212529">
          {total}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {segments.map((seg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
            <span>{seg.label}</span>
            <span className="adm-mono" style={{ marginLeft: 4, color: '#495057' }}>
              {((seg.value / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export const UsageAnalytics: React.FC = () => {
  const [rangeIdx, setRangeIdx] = useState(1); // default: 30d
  const range = TIME_RANGES[rangeIdx];

  const metrics = useMetrics(range.hours, 120_000);
  const feedback = useFeedback(Math.ceil(range.hours / 24), 120_000);

  const d = metrics.data;
  const fb = feedback.data;

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">Usage Analytics</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {TIME_RANGES.map((tr, i) => (
            <button
              key={i}
              className={`adm-btn adm-btn-sm ${i === rangeIdx ? 'adm-btn-active' : ''}`}
              onClick={() => setRangeIdx(i)}
            >
              {tr.label}
            </button>
          ))}
          <span className="adm-refresh-label" style={{ marginLeft: 8 }}>
            Updated {formatRelativeTime(metrics.lastRefreshed)}
          </span>
        </div>
      </div>

      {metrics.loading && !d ? (
        <LoadingPanel message="Loading analytics..." />
      ) : metrics.error && !d ? (
        <ErrorPanel message={metrics.error} onRetry={metrics.refresh} />
      ) : d ? (
        <>
          {/* KPI Row */}
          <div className="adm-kpi-row">
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">{d.total_conversations?.toLocaleString() ?? d.total_queries?.toLocaleString() ?? 0}</div>
              <div className="adm-kpi-label">Conversations</div>
            </div>
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">{d.total_queries?.toLocaleString() ?? 0}</div>
              <div className="adm-kpi-label">Messages</div>
            </div>
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">
                {fb && fb.satisfaction_rate != null ? `${fb.satisfaction_rate.toFixed(1)}%` : '—'}
              </div>
              <div className="adm-kpi-label">Satisfaction Rate</div>
            </div>
            <div className="adm-kpi-card">
              <div className="adm-kpi-value adm-mono">{formatResponseTime(d.avg_response_time)}</div>
              <div className="adm-kpi-label">Avg Response Time</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="adm-charts-row">
            {/* Daily Volume */}
            <div className="adm-card" style={{ flex: 2 }}>
              <div className="adm-card-title">Daily Query Volume</div>
              {d.daily_volumes && d.daily_volumes.length > 0 ? (
                <BarChart data={d.daily_volumes.map((v) => ({ label: v.date, value: v.count }))} />
              ) : (
                <p className="adm-muted">Daily breakdown not available for this period.</p>
              )}
            </div>

            {/* Model Routing */}
            <div className="adm-card" style={{ flex: 1 }}>
              <div className="adm-card-title">Model Routing</div>
              {d.model_usage ? (
                <DonutChart
                  segments={[
                    { label: 'GPT-4o-mini', value: d.model_usage['gpt-4o-mini'] ?? 0, color: 'var(--doyle-blue)' },
                    { label: 'GPT-4o', value: d.model_usage['gpt-4o'] ?? 0, color: '#6C757D' },
                  ]}
                />
              ) : (
                <DonutChart
                  segments={[
                    { label: 'GPT-4o-mini (~90%)', value: 90, color: 'var(--doyle-blue)' },
                    { label: 'GPT-4o (~10%)', value: 10, color: '#6C757D' },
                  ]}
                />
              )}
            </div>
          </div>

          {/* Content Gaps */}
          <div className="adm-card" style={{ marginTop: 16 }}>
            <div className="adm-card-title">Content Gaps</div>
            {d.content_gaps && d.content_gaps.length > 0 ? (
              <table className="adm-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Query Pattern</th>
                    <th>Count</th>
                    <th>Avg Relevance</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {d.content_gaps.map((gap, i) => (
                    <tr key={i}>
                      <td>{gap.query_pattern}</td>
                      <td className="adm-mono">{gap.count}</td>
                      <td className="adm-mono">{gap.avg_relevance.toFixed(3)}</td>
                      <td>
                        <a
                          href="https://godoyle.sharepoint.com/sites/DoyleIntranet/Knowledge%20Base/_Published"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="adm-link"
                        >
                          Add content
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="adm-muted" style={{ marginTop: 8 }}>
                No significant content gaps detected in this period.
              </p>
            )}
          </div>

          {/* Top Users */}
          {d.top_users && d.top_users.length > 0 && (
            <div className="adm-card" style={{ marginTop: 16 }}>
              <div className="adm-card-title">Top Users</div>
              <table className="adm-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Messages</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {d.top_users.slice(0, 15).map((u, i) => {
                    const total = d.total_queries || d.top_users!.reduce((s, x) => s + x.count, 0);
                    const pct = total > 0 ? (u.count / total) * 100 : 0;
                    return (
                      <tr key={i}>
                        <td className="adm-mono">{i + 1}</td>
                        <td>{u.user}</td>
                        <td className="adm-mono">{u.count}</td>
                        <td className="adm-mono">{pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Top Queries */}
          {d.top_queries && d.top_queries.length > 0 && (
            <div className="adm-card" style={{ marginTop: 16 }}>
              <div className="adm-card-title">Top Queries</div>
              <table className="adm-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Query</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {d.top_queries.slice(0, 10).map((q, i) => (
                    <tr key={i}>
                      <td className="adm-mono">{i + 1}</td>
                      <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.query}</td>
                      <td className="adm-mono">{q.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
